/**
 * PDF アップロード共通コンポーネント。
 * - Firebase Storage に直接アップロード（uploadBytesResumable で進捗表示）
 * - パス: offerings/{offeringId}/{timestamp}_{fileName}.pdf
 * - サーバー側 Storage トリガー (processOfferingPdf) で pdf-parse → Firestore 反映
 * - 親コンポーネントは Firestore の onSnapshot で pdfFiles 更新を監視する想定
 */
import type { OfferingPdfFile } from '@goenhub/shared';
import { ExternalLink, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { storage } from '@/lib/firebase';
import {
  deleteObject,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';

const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

interface PdfUploaderProps {
  offeringId: string;
  pdfFiles: OfferingPdfFile[];
  disabled?: boolean;
}

interface UploadingFile {
  fileName: string;
  progress: number; // 0-100
}

export function PdfUploader({ offeringId, pdfFiles, disabled }: PdfUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<UploadingFile | null>(null);
  const [pendingExtraction, setPendingExtraction] = useState(false);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_PDF_SIZE_BYTES) {
      return `ファイルサイズが上限 (${MAX_PDF_SIZE_BYTES / 1024 / 1024}MB) を超えています`;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      return 'PDF ファイル (.pdf) のみアップロードできます';
    }
    return null;
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    const safeName = file.name.replace(/[^\w.\-ぁ-んァ-ヶ一-龯]/g, '_');
    const storagePath = `offerings/${offeringId}/${Date.now()}_${safeName}`;
    const fileRef = storageRef(storage, storagePath);

    setUploading({ fileName: file.name, progress: 0 });

    const task = uploadBytesResumable(fileRef, file, {
      contentType: 'application/pdf',
    });

    task.on(
      'state_changed',
      (snapshot) => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploading({ fileName: file.name, progress: pct });
      },
      (err) => {
        setUploading(null);
        toast.error(`アップロード失敗: ${err.message}`);
      },
      () => {
        setUploading(null);
        setPendingExtraction(true);
        toast.success('アップロード完了。テキスト抽出を実行中です...');
        // Cloud Function 完了まで Firestore 監視に任せる。
        // 安全網として 30 秒で抽出待機表示を解除。
        setTimeout(() => setPendingExtraction(false), 30_000);
      },
    );

    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDelete = async (pdf: OfferingPdfFile) => {
    if (!window.confirm(`「${pdf.fileName}」を削除します。よろしいですか？`)) return;
    try {
      await deleteObject(storageRef(storage, pdf.storagePath));
      toast.success('削除しました');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`削除に失敗しました: ${msg}`);
    }
  };

  return (
    <div className="space-y-2">
      {pdfFiles.length === 0 && !uploading && !pendingExtraction && (
        <p className="text-xs text-[var(--text-muted)] italic">
          PDF は未登録です
        </p>
      )}

      {pdfFiles.map((pdf) => (
        <div
          key={pdf.storagePath}
          className="flex items-center gap-2 p-2 bg-[var(--bg-elevated)] rounded-sm"
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{pdf.fileName}</div>
            <div className="text-xs text-[var(--text-muted)] flex items-center gap-2">
              <span>{(pdf.size / 1024).toFixed(1)} KB</span>
              <span>抽出済み: {pdf.text?.length ?? 0} 文字</span>
            </div>
          </div>
          {pdf.downloadUrl && (
            <a
              href={pdf.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title="プレビュー"
            >
              <ExternalLink className="size-4" />
            </a>
          )}
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(pdf)}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      ))}

      {uploading && (
        <div className="p-2 bg-[var(--bg-elevated)] rounded-sm">
          <div className="text-sm truncate">{uploading.fileName}</div>
          <div className="mt-1 h-1.5 bg-[var(--border)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--accent)] transition-all"
              style={{ width: `${uploading.progress}%` }}
            />
          </div>
          <div className="text-xs text-[var(--text-muted)] mt-1">
            アップロード中 {uploading.progress}%
          </div>
        </div>
      )}

      {pendingExtraction && !uploading && (
        <div className="text-xs text-[var(--text-muted)] italic">
          テキスト抽出中...（数秒で一覧に反映されます）
        </div>
      )}

      {!disabled && (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={!!uploading}
          >
            <Upload />
            {uploading ? 'アップロード中...' : 'PDF を追加'}
          </Button>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            最大 10MB / PDF 形式のみ。アップロード後、自動でテキストが抽出されます。
          </p>
        </div>
      )}
    </div>
  );
}
